import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { VerificationCompletionData } from '../../@types/VerificationCompletionData';
import { VerificationStatus } from '../../@types/VerificationStatus';
import VerificationStatusCheck from '../../components/verificationStatusCheck/VerificationStatusCheck';
import { VerificationMethod } from '../../@types/VerificationMethod';
import { trackEvent } from '../../tracking/tracking';
import { useClientConfig } from '../../utils/clientConfigContext';
import { getWebIdSessionVerifiedAt } from '../../api/webid/webIdVerification';
import { useTvsState } from '../../utils/useTvsState';

const POLLING_INTERVAL_MS = 1000;
const PROGRESS_INCREMENT = 20;
const MAX_PROGRESS = 95;
const COMPLETION_DELAY_MS = 1000;
const MAX_VERIFICATION_TIME_MS = 15000;
const CLOSE_BUTTON_DELAY_MS = 5000;
const PROGRESS_COMPLETE_TIME_MS = 14000;

interface WebIdStatusCheckPageProps {
  onVerificationComplete?: (status: VerificationStatus, data: VerificationCompletionData) => void;
}

const WebIdStatusCheckPage: React.FC<WebIdStatusCheckPageProps> = ({ onVerificationComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isChecking, setIsChecking] = useState(true);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [showFullProgress, setShowFullProgress] = useState(false);
  const [searchParams] = useSearchParams();
  const [, setTvsState] = useTvsState();
  const { clientId, hideToasterOnSuccess, userId } = useClientConfig();
  const sessionId = searchParams.get('tvs_session_id') || '';

  useEffect(() => {
    let verificationTimer: NodeJS.Timeout;
    let progressTimer: NodeJS.Timeout;
    let maxVerificationTimer: NodeJS.Timeout;
    let closeButtonTimer: NodeJS.Timeout;
    let progressCompleteTimer: NodeJS.Timeout;
    let completionTimer: NodeJS.Timeout;

    const checkVerificationStatus = async () => {
      if (!sessionId) {
        handleVerificationTimeout();
        return;
      }

      try {
        const response = await getWebIdSessionVerifiedAt(sessionId, userId);

        if (response.verifiedAt) {
          handleVerificationSuccess();
        } else if (response.status === 'DECLINED' || response.status === 'EXPIRED') {
          handleVerificationTimeout();
        }
      } catch (error) {
        console.log('WebID verification check error, continuing to check...', error);
      }
    };

    const updateProgress = () => {
      setProgress((prevProgress) => Math.min(prevProgress + PROGRESS_INCREMENT, MAX_PROGRESS));
    };

    const handleVerificationSuccess = () => {
      setShowFullProgress(true);
      setProgress(100);
      cleanup();
      completionTimer = setTimeout(() => {
        setIsChecking(false);
        trackEvent('is24.de.sicherheit.verifizierung.webid', 'verification_successful', 'user', clientId, 'webid');
        if (hideToasterOnSuccess) {
          setTvsState('');
        } else {
          setTvsState('verification_success');
        }
        onVerificationComplete?.(VerificationStatus.SUCCESS, {
          sessionId,
          verificationMethod: VerificationMethod.WebId,
        });
      }, COMPLETION_DELAY_MS);
    };

    const handleVerificationTimeout = () => {
      setShowFullProgress(true);
      setProgress(100);
      cleanup();
      completionTimer = setTimeout(() => {
        setIsChecking(false);
        setTvsState('verification_failure_webid');
      }, COMPLETION_DELAY_MS);
    };

    const cleanup = () => {
      clearTimeout(maxVerificationTimer);
      clearTimeout(closeButtonTimer);
      clearTimeout(progressCompleteTimer);
      clearInterval(progressTimer);
      clearInterval(verificationTimer);
    };

    if (isChecking) {
      maxVerificationTimer = setTimeout(handleVerificationTimeout, MAX_VERIFICATION_TIME_MS);
      closeButtonTimer = setTimeout(() => {
        setShowCloseButton(true);
      }, CLOSE_BUTTON_DELAY_MS);
      progressCompleteTimer = setTimeout(() => {
        setShowFullProgress(true);
        setProgress(100);
      }, PROGRESS_COMPLETE_TIME_MS);
      verificationTimer = setInterval(checkVerificationStatus, POLLING_INTERVAL_MS);
      progressTimer = setInterval(updateProgress, POLLING_INTERVAL_MS);
      checkVerificationStatus();
      return () => {
        cleanup();
        clearTimeout(completionTimer);
      };
    }
  }, [clientId, hideToasterOnSuccess, isChecking, onVerificationComplete, sessionId, setTvsState, userId]);

  if (!isChecking) return null;

  const finalProgress = showFullProgress ? 100 : progress;

  return (
    <VerificationStatusCheck
      progress={finalProgress}
      verificationMethod={VerificationMethod.WebId}
      showCloseButton={showCloseButton}
      onClose={() => {
        setIsChecking(false);
        setTvsState('verification_failure_webid');
      }}
    />
  );
};

export default WebIdStatusCheckPage;
